import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, confirmDialog } from "@/lib/ui";
import { Users, Mail, Shield, X, Copy, Check, Send } from "lucide-react";

const RELATIONS = [
  ["wali", "Wali"],
  ["father", "Far"],
  ["mother", "Mor"],
  ["brother", "Bror"],
  ["sister", "Søster"],
  ["uncle", "Onkel"],
  ["aunt", "Tante"],
  ["other", "Andet familiemedlem"],
];

export default function FamilyCircle() {
  const [invites, setInvites] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", relation: "wali" });
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/family/invites");
      setInvites(data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    setBusy(true); setErr("");
    try {
      const origin_url = window.location.origin;
      await api.post("/family/invite", { ...form, origin_url });
      setForm({ name: "", email: "", relation: "wali" });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    setBusy(false);
  };

  const revoke = async (id) => {
    if (!confirm("Er du sikker? Personen mister adgangen med det samme.")) return;
    try {
      await api.delete(`/family/invites/${id}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-7" data-testid="family-page">
      <div className="eyebrow flex items-center gap-2"><Shield className="h-3 w-3" /> Familie-cirkel</div>
      <h1 className="mt-2 font-serif text-4xl text-olive-700">Inviter en wali eller familie</h1>
      <p className="mt-2 text-brown-600 max-w-2xl text-sm">
        En pårørende du stoler på kan følge din rejse read-only — de ser antal matches og introduktioner, men aldrig
        indholdet af private beskeder. Barakah, uden at kompromittere dit privatliv.
      </p>

      <div className="mt-8 card-soft p-6" data-testid="invite-form">
        <div className="grid gap-3 md:grid-cols-3">
          <label className="block">
            <span className="text-sm text-brown-600">Navn</span>
            <input className="field w-full mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Fx Muhammad Ali" data-testid="invite-name" />
          </label>
          <label className="block">
            <span className="text-sm text-brown-600">E-mail</span>
            <input type="email" className="field w-full mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="wali@eksempel.dk" data-testid="invite-email" />
          </label>
          <label className="block">
            <span className="text-sm text-brown-600">Relation</span>
            <select className="field w-full mt-1" value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })} data-testid="invite-relation">
              {RELATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>
        {err && <div className="mt-3 text-destructive text-sm" data-testid="invite-error">{err}</div>}
        <div className="mt-4 flex justify-end">
          <button onClick={invite} disabled={busy || !form.name || !form.email} className="btn-primary disabled:opacity-60" data-testid="invite-send">
            <Send className="h-4 w-4" /> {busy ? "Sender…" : "Send invitation"}
          </button>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="font-serif text-2xl text-olive-700">Aktive inviterede</h2>
        {invites.length === 0 && <div className="mt-4 text-brown-500 text-sm" data-testid="invites-empty">Ingen aktive invitationer endnu.</div>}
        <div className="mt-4 space-y-3">
          {invites.map((inv) => {
            const link = `${window.location.origin}/wali?invite=${inv.id}`;
            return (
              <div key={inv.id} className="card-soft p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`invite-row-${inv.id}`}>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-olive-100 text-olive-700 flex items-center justify-center"><Users className="h-5 w-5" /></div>
                  <div>
                    <div className="font-medium text-olive-700">{inv.wali_name} <span className="text-xs text-brown-500 font-normal">({inv.relation})</span></div>
                    <div className="text-xs text-brown-500 flex items-center gap-1"><Mail className="h-3 w-3" /> {inv.wali_email}</div>
                    <div className="text-[10px] text-brown-400 mt-1">
                      {inv.accepted_at ? `Accepteret ${new Date(inv.accepted_at).toLocaleDateString("da-DK")}` : "Afventer accept"}
                      {inv.last_viewed_at && ` · Sidst set ${new Date(inv.last_viewed_at).toLocaleDateString("da-DK")}`}
                    </div>
                  </div>
                </div>
                <button onClick={() => revoke(inv.id)} className="btn-ghost text-xs text-destructive" data-testid={`revoke-${inv.id}`}>
                  <X className="h-3 w-3" /> Tilbagekald
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
